export type AmazonAccessStatus = "none" | "invite_sent" | "verified";

export type BrandAmazonAccess = {
  id: string;
  name: string;
  brandRegistry: boolean;
  brandRegistryAnsweredAt: string | null;
  collaboratorStatus: AmazonAccessStatus;
  inviteSentAt: string | null;
  verifiedAt: string | null;
};

export type AmazonTakedownCapability =
  | "unknown"
  | "unverified_no_brand_registry"
  | "brand_registry_access_required"
  | "brand_registry_access_pending"
  | "brand_registry_ready";

export function amazonTakedownCapability(
  brand: Pick<
    BrandAmazonAccess,
    "brandRegistry" | "brandRegistryAnsweredAt" | "collaboratorStatus"
  >
): AmazonTakedownCapability {
  const registryAnswerKnown =
    brand.brandRegistry || Boolean(brand.brandRegistryAnsweredAt);
  if (!registryAnswerKnown) return "unknown";
  if (!brand.brandRegistry) return "unverified_no_brand_registry";
  if (brand.collaboratorStatus === "verified") {
    return "brand_registry_ready";
  }
  if (brand.collaboratorStatus === "invite_sent") {
    return "brand_registry_access_pending";
  }
  return "brand_registry_access_required";
}

export function canAuthorizeManagedTakedowns(
  brand: Pick<
    BrandAmazonAccess,
    "brandRegistry" | "brandRegistryAnsweredAt" | "collaboratorStatus"
  >
): boolean {
  return amazonTakedownCapability(brand) === "brand_registry_ready";
}

export function amazonTakedownCapabilityLabel(
  capability: AmazonTakedownCapability
): string {
  if (capability === "brand_registry_ready") return "Takedowns ready";
  if (capability === "brand_registry_access_pending") return "Access pending";
  if (capability === "brand_registry_access_required") return "Access required";
  if (capability === "unverified_no_brand_registry") {
    return "Takedowns unavailable";
  }
  return "Registry status needed";
}

export function amazonAccessProgress(brands: BrandAmazonAccess[]) {
  const capabilities = brands.map(amazonTakedownCapability);
  return {
    total: brands.length,
    complete: capabilities.filter(
      (capability) =>
        capability === "brand_registry_ready" ||
        capability === "unverified_no_brand_registry"
    ).length,
    takedownReady: capabilities.filter(
      (capability) => capability === "brand_registry_ready"
    ).length,
    waiting: capabilities.filter(
      (capability) => capability === "brand_registry_access_pending"
    ).length,
    pending: capabilities.filter(
      (capability) =>
        capability === "unknown" ||
        capability === "brand_registry_access_required"
    ).length,
  };
}

export function amazonAccessLabel(status: AmazonAccessStatus): string {
  if (status === "verified") return "Access verified";
  if (status === "invite_sent") return "Invitation sent";
  return "Access not started";
}
